import { forwardRef, ComponentPropsWithoutRef } from "react"
import classNames from "classnames"
import { Text } from ".."
import { devWarning } from "../utils/devWarning"
import { Sizes } from "@oysterhr/common"

type ProgressCircleSize = Extract<Sizes, "sm" | "md" | "lg">

interface ProgressCircleProps extends ComponentPropsWithoutRef<"div"> {
  /**
   * The literal value of the progress.
   * @default 0
   */
  value?: number
  /**
   * The allowable maximum literal value of the progress.
   * @default 100
   */
  max?: number
  /**
   * The label of the `ProgressCircle`. The value of this prop is also used as
   * the "aria-label" of the progress. This prop is highly recommended to make
   * the component ARIA-compliant.
   */
  label: string
  /**
   * When true, visually hides the label
   */
  visuallyHideLabel?: boolean
  /**
   * A custom text that specifies the current progress. If this text is
   * provided, it is displayed in place of the progress percentage.
   */
  progressText?: string
  /**
   * The size of the component.
   * @default md
   */
  size?: ProgressCircleSize
}

const strokeWidthMapping: Record<ProgressCircleSize, number> = {
  sm: 6,
  md: 12,
  lg: 15,
}

const progressTextStylesMapping: Record<ProgressCircleSize, string> = {
  sm: "text-base leading-6 tracking-md",
  md: "text-2xl leading-8 tracking-2xl",
  lg: "text-4xl leading-11 tracking-3xl",
}

function getProgressValue(value: number, max: number): number {
  if (value < 0) {
    devWarning(true, 'The ProgressCircle "value" is less than 0.')
    return 0
  }

  if (value > max) {
    devWarning(true, 'The ProgressCircle "value" is greater than the "max" value.')
    return max
  }

  return value
}

export const ProgressCircle = forwardRef<HTMLDivElement, ProgressCircleProps>(
  (
    {
      children,
      className: classNameProp,
      label,
      max = 100,
      progressText: progressTextProp,
      size = "md",
      value = 0,
      visuallyHideLabel: visuallyHideLabelProp = false,
      ...restProps
    },
    ref,
  ) => {
    const containerSideLength = size === "lg" ? 200 : size === "md" ? 120 : 64
    const svgCircleDiameter = containerSideLength
    const _value = getProgressValue(value, max)
    const progressPercent = Math.round((_value / max) * 100) || 0

    const progressText = progressTextProp || progressPercent + "%"

    const strokeWidth = strokeWidthMapping[size]
    const radius = svgCircleDiameter / 2 - strokeWidth / 2
    const circumference = 2 * Math.PI * radius
    const percentInCircumferenceTerm = (progressPercent / 100) * circumference
    const offset = circumference - percentInCircumferenceTerm

    // The label is always visually hidden for "sm" `ProgressCircle`
    const visuallyHideLabel = size === "sm" ? true : visuallyHideLabelProp

    return (
      <div
        aria-label={label}
        aria-valuemax={max}
        aria-valuemin={0}
        aria-valuenow={_value}
        aria-valuetext={progressText}
        className={classNames(classNameProp, "inline-grid")}
        ref={ref}
        role="progressbar"
        style={{ width: containerSideLength, height: containerSideLength }}
        {...restProps}
      >
        <div className="col-start-1 col-end-2 row-start-1 row-end-2">
          <svg
            width={containerSideLength}
            height={containerSideLength}
            className={"-rotate-90 fill-white"}
          >
            <circle
              r={radius}
              cx="50%"
              cy="50%"
              className="stroke-gray-200"
              strokeWidth={strokeWidth}
            />
            <circle
              r={radius}
              cx="50%"
              cy="50%"
              strokeLinecap="round"
              strokeWidth={strokeWidth}
              strokeDasharray={circumference}
              strokeDashoffset={offset}
              className="stroke-success-lighten-50 transition-[stroke,_stroke-dashoffset] duration-300"
            />
          </svg>
        </div>

        <div
          className={classNames(
            // relative ensures that this displays above the svg, which is a statically positioned element
            "relative col-start-1 col-end-2 row-start-1 row-end-2 flex flex-col items-center justify-center text-center",
          )}
        >
          <div
            className={classNames({
              "w-8": size === "sm",
              "w-20": size === "md",
              "w-36": size === "lg",
            })}
          >
            <Text
              size={size === "lg" ? "medium" : "tiny"}
              className={classNames(visuallyHideLabel && "sr-only")}
            >
              {label}
            </Text>
            <Text
              className={classNames("font-semibold text-black", progressTextStylesMapping[size])}
            >
              {progressText}
            </Text>
          </div>
        </div>
      </div>
    )
  },
)

ProgressCircle.displayName = "ProgressCircle"
